import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { productStructureApi, type ProductRelationship, type RelationshipType } from '../../api/productstructure';

const REL_TYPES: RelationshipType[] = [
  'cross_sell', 'up_sell', 'frequently_bought_together', 'similar', 'accessory', 'dependency', 'related',
];

export function RelationshipManagerPage() {
  const [items, setItems] = useState<ProductRelationship[]>([]);
  const [source, setSource] = useState('');
  const [target, setTarget] = useState('');
  const [type, setType] = useState<RelationshipType>('cross_sell');
  const [error, setError] = useState('');

  const reload = () => {
    void productStructureApi.listRelationships(source ? { source_product_id: source } : undefined).then(setItems).catch((e) => setError(String(e)));
  };

  useEffect(() => { reload(); }, [source]);

  const create = async () => {
    if (!source.trim() || !target.trim()) return;
    try {
      await productStructureApi.createRelationship({ source_product_id: source.trim(), target_product_id: target.trim(), type });
      setTarget('');
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-structure" className="text-sm text-slate-600 hover:underline">← Structure hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Relationship Manager</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Source product ID" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Target product ID" className="rounded-lg border px-3 py-2 text-sm" />
        <select value={type} onChange={(e) => setType(e.target.value as RelationshipType)} className="rounded-lg border px-3 py-2 text-sm">
          {REL_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Add relationship</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left text-slate-600">
          <tr><th className="px-4 py-3">Source</th><th className="px-4 py-3">Target</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th></tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-b">
              <td className="px-4 py-3">{r.source_product_id}</td>
              <td className="px-4 py-3">{r.target_product_id}</td>
              <td className="px-4 py-3">{r.type}</td>
              <td className="px-4 py-3">{r.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ProductFamilyPage() {
  const [items, setItems] = useState<{ id: string; name: string; product_ids?: string[] }[]>([]);
  const [name, setName] = useState('');
  const [productIds, setProductIds] = useState('');
  const [error, setError] = useState('');

  const reload = () => {
    void productStructureApi.listFamilies().then(setItems).catch((e) => setError(String(e)));
  };

  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await productStructureApi.createFamily({
        name: name.trim(),
        product_ids: productIds.split(',').map((s) => s.trim()).filter(Boolean),
      });
      setName('');
      setProductIds('');
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-structure" className="text-sm text-slate-600 hover:underline">← Structure hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Product Families</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Family name" className="rounded-lg border px-3 py-2 text-sm" />
        <input value={productIds} onChange={(e) => setProductIds(e.target.value)} placeholder="Product IDs" className="min-w-[240px] rounded-lg border px-3 py-2 text-sm" />
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Create family</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <ul className="mt-6 space-y-2 rounded-xl border bg-white p-4 text-sm">
        {items.map((f) => (
          <li key={f.id} className="flex justify-between border-b py-2 last:border-0">
            <span className="font-medium">{f.name}</span>
            <span className="text-slate-500">{f.product_ids?.length ?? 0} products</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MarketplaceStructurePage() {
  const [items, setItems] = useState<{ id: string; product_id: string; marketplace: string; type: string }[]>([]);
  const [productId, setProductId] = useState('');
  const [marketplace, setMarketplace] = useState('amazon');
  const [error, setError] = useState('');

  const reload = () => {
    void productStructureApi.listMarketplaceRelationships(productId ? { product_id: productId } : undefined).then(setItems).catch((e) => setError(String(e)));
  };

  useEffect(() => { reload(); }, [productId]);

  const create = async () => {
    if (!productId.trim()) return;
    try {
      await productStructureApi.createMarketplaceRelationship({ product_id: productId.trim(), marketplace, type: 'related' });
      reload();
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-structure" className="text-sm text-slate-600 hover:underline">← Structure hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Marketplace Structure</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        <input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="Product ID" className="rounded-lg border px-3 py-2 text-sm" />
        <select value={marketplace} onChange={(e) => setMarketplace(e.target.value)} className="rounded-lg border px-3 py-2 text-sm">
          <option value="amazon">Amazon</option>
          <option value="google">Google</option>
          <option value="shopify">Shopify</option>
        </select>
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">Map structure</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <table className="mt-6 min-w-full rounded-xl border bg-white text-sm">
        <thead className="border-b bg-slate-50 text-left"><tr><th className="px-4 py-3">Product</th><th className="px-4 py-3">Marketplace</th><th className="px-4 py-3">Type</th></tr></thead>
        <tbody>
          {items.map((m) => (
            <tr key={m.id} className="border-b">
              <td className="px-4 py-3">{m.product_id}</td>
              <td className="px-4 py-3">{m.marketplace}</td>
              <td className="px-4 py-3">{m.type}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DependencyGraphPage() {
  const [items, setItems] = useState<ProductRelationship[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    void productStructureApi.listRelationships({ type: 'dependency' }).then(setItems).catch((e) => setError(String(e)));
  }, []);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/product-structure" className="text-sm text-slate-600 hover:underline">← Structure hub</Link>
      <h1 className="mt-4 text-2xl font-semibold">Dependency Graph</h1>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 rounded-xl border bg-white p-6">
        {items.length === 0 ? (
          <p className="text-slate-500">No dependency relationships yet.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {items.map((r) => (
              <li key={r.id} className="rounded-lg border px-4 py-3">
                <span className="font-medium">{r.source_product_id}</span>
                <span className="mx-2 text-slate-400">→ depends on →</span>
                <span className="font-medium">{r.target_product_id}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
